import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { FeaturedListingCard } from './FeaturedListingCard';

export type VehicleCarouselTab = {
  id: string;
  label: string;
};

interface VehicleCarouselSectionProps {
  id?: string;
  title: string;
  viewAllHref: string;
  viewAllLabel?: string;
  vehicles: any[];
  tabs?: VehicleCarouselTab[];
  activeTab?: string;
  onTabChange?: (tabId: string) => void;
  muted?: boolean;
  headerAction?: ReactNode;
  showViewAll?: boolean;
}

export function VehicleCarouselSection({
  id,
  title,
  viewAllHref,
  viewAllLabel = 'Ver tudo',
  vehicles,
  tabs,
  activeTab,
  onTabChange,
  muted = false,
  headerAction,
  showViewAll = true,
}: VehicleCarouselSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > 4);
    setCanScrollNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateScrollState();
    const t = setTimeout(updateScrollState, 150);

    el.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      clearTimeout(t);
      el.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [vehicles]);

  const scroll = (direction: 'prev' | 'next') => {
    const el = scrollRef.current;
    if (!el) return;
    const firstItem = el.querySelector<HTMLElement>('.wl6-featured-carousel-item');
    const gap = 20;
    const amount = firstItem ? firstItem.offsetWidth + gap : el.clientWidth * 0.8;
    el.scrollBy({ left: direction === 'next' ? amount : -amount, behavior: 'smooth' });
  };

  if (vehicles.length === 0) return null;

  const showNav = vehicles.length > 1;

  return (
    <section
      id={id}
      className={`wl6-featured-section${muted ? ' wl6-featured-section--muted' : ''}`}
    >
      <div className="wl6-container">
        <div className="wl6-featured-head">
          <h2 className="wl6-featured-title">{title}</h2>
          <div className="wl6-featured-head-actions">
            {headerAction}
            {showViewAll && (
              <Link to={viewAllHref} className="wl6-featured-view-all">
                {viewAllLabel}
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            )}
          </div>
        </div>

        {tabs && tabs.length > 0 && (
          <div className="wl6-featured-tabs-wrap">
            <div className="wl6-featured-tabs" role="tablist">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    className={`wl6-featured-tab${isActive ? ' is-active' : ''}`}
                    onClick={() => onTabChange?.(tab.id)}
                  >
                    {tab.label}
                    {isActive && <span className="wl6-featured-tab-line" />}
                  </button>
                );
              })}
            </div>
            <div className="wl6-featured-tabs-rule" />
          </div>
        )}

        <div className="wl6-featured-carousel-wrap">
          <div ref={scrollRef} className="wl6-featured-carousel">
            {vehicles.map((vehicle) => (
              <div key={vehicle.id} className="wl6-featured-carousel-item">
                <FeaturedListingCard vehicle={vehicle} />
              </div>
            ))}
          </div>

          {showNav && (
            <div className="wl6-featured-nav">
              <button
                type="button"
                className="wl6-featured-nav-btn"
                onClick={() => scroll('prev')}
                disabled={!canScrollPrev}
                aria-label="Veículos anteriores"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="wl6-featured-nav-btn"
                onClick={() => scroll('next')}
                disabled={!canScrollNext}
                aria-label="Próximos veículos"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
